import React from 'react'
import { v1 as uuidV1 } from 'uuid'
import { FormInput, FormInputProps } from 'semantic-ui-react'
import { Field, WrappedFieldProps } from 'redux-form'
import { ReduxFieldProps, SemanticFieldProps, mapErrorProps } from './SemanticField'
import { useRegisterTab } from './Tabs/TabItem'

interface AdditionalProps {
  autoComplete?: 'on' | 'off' | 'new-password'
  clearable?: boolean
}

export interface StrippedInputProps extends Omit<FormInputProps, 'input'>, AdditionalProps {}

export type ReduxInputProps = SemanticFieldProps<StrippedInputProps>
type InnerFieldProps = ReduxFieldProps<StrippedInputProps>

const Component = (props: InnerFieldProps & WrappedFieldProps) => {
  const { autoComplete, input, meta, label, clearable, 'data-testid': dataTestId, ...rest } = props

  const onClear = React.useCallback(() => input.onChange(''), [input.onChange])
  const clearableConfig = React.useMemo(
    () => ({
      icon: 'delete',
      onClick: onClear,
      type: 'button',
      'data-testid': 'input-clear-button'
    }),
    [onClear]
  )

  // use randomized input name to prevent autocompletion
  const name = React.useMemo(() => (autoComplete === 'on' ? input.name : `${uuidV1()}-${input.name}`), [autoComplete, input.name])

  const inputProps = props.children ? undefined : { 'data-testid': dataTestId }

  return (
    <FormInput
      input={inputProps}
      action={clearable && clearableConfig}
      id={input.name}
      {...input}
      {...rest}
      name={name}
      autoComplete={autoComplete}
      {...mapErrorProps(meta, label)}
    />
  )
}

Component.defaultProps = {
  autoComplete: 'off'
}

export const ReduxInput: React.FC<ReduxInputProps> = props => {
  useRegisterTab(props.name)

  return <Field component={Component} {...props} />
}

ReduxInput.displayName = 'ReduxInput'

export default React.memo(ReduxInput)
